import { Play } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { BattleInfo } from "../shared-sortaballs/PuzzleHeader"

import { fireSuccessFeedbackCanvas } from "../modules/feedback-canvas"
import {
  clearCurrentPuzzle,
  loadCurrentPuzzleBoard,
} from "../shared-sortaballs/current-puzzle-storage"
import { useCurrentPuzzleBoardPersist } from "../shared-sortaballs/use-current-puzzle-board-persist"
import {
  GameplayTourView,
  useGameplayTour,
} from "../shared-sortaballs/gameplay-tour"
import { PuzzleHeader as SharedPuzzleHeader } from "../shared-sortaballs/PuzzleHeader"
import { useHelperReplay } from "../shared-sortaballs/use-helper-replay"
import { advanceBoard } from "../sortaballs-challenges/advance-board"
import { Board } from "./Board"
import { HARD_CAPACITY } from "./hard-state"
import { sortaballs8ColorsLevelStore } from "./level-store"
import { DEFAULT_CAPACITY, isOneMoveFromSolved, isSolved } from "./logic"
import { getPuzzleById, getTotalPuzzleCount } from "./puzzle-loader"
import { checkIfSolved, saveProgress } from "./puzzle-progress"
import { useBallSort } from "./use-ball-sort"
import "./sortaballs-8-colors.css"
import "./puzzle-ui.css"

export function Sortaballs8ColorsApp(
  input: {
    advancedBy?: number
    battle?: BattleInfo
    hideHints?: boolean
  } = {},
) {
  // Current puzzle comes from the shared level store, so the sidebar panel
  // and the game page stay in sync (persisted to localStorage by the store).
  const currentPuzzleId = sortaballs8ColorsLevelStore.useLevel()
  const { advancedBy = 0, battle, hideHints = false } = input

  // Get current puzzle object
  const currentPuzzle = getPuzzleById(currentPuzzleId) ?? null

  // Accepted-challenge opening: while `isAnimatingOpen` the engine runs from
  // the ORIGINAL board; the pre-roll plays the creator's hint moves once, then
  // the initial flips to the advanced board so Reset never re-plays them.
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(advancedBy > 0)
  const preRolledRef = useRef(false)

  // Restored session for the current puzzle (null until one was saved). The
  // saved move list lets a reload REPLAY the session — the engine rebuilds
  // history and undo from it instead of seeding a bare board.
  const restored = useMemo(()=> {
    if (!currentPuzzle || battle) return null
    return loadCurrentPuzzleBoard(currentPuzzle.boardId)
  }, [currentPuzzle, battle])

  const { capacity, initialState } = useMemo(()=> {
    if (currentPuzzle) {
      // During the opening pre-roll the acceptor starts from the CLEAN
      // (original) board, so the programmatic hint reveal replays the
      // creator's moves from scratch — the same visuals as pressing the
      // (hidden) hint button. The advanced end-state only becomes the
      // engine's initial board once the reveal finishes (isAnimatingOpen →
      // false), so a later Reset targets the hints-applied board and the
      // animation can never re-run.
      const board = isAnimatingOpen
        ? currentPuzzle.state
        : advanceBoard(
            currentPuzzle.state,
            currentPuzzle.correctMoves ?? [],
            advancedBy,
          )
      return { capacity: HARD_CAPACITY, initialState: board }
    }
    return { capacity: DEFAULT_CAPACITY, initialState: undefined }
  }, [currentPuzzle, advancedBy, isAnimatingOpen])

  const {
    board,
    clearMovingBall,
    clearSelection,
    commitSelectPlate,
    demoSelectPlate,
    flightKey,
    groupSize,
    moveCount,
    moves,
    movingBall,
    reset,
    selectAll,
    selectedPlate,
    selectionCount,
    selectPlate,
    undo,
  } = useBallSort(initialState, capacity, restored?.moves)

  const { isPlaying, startReplay } = useHelperReplay({
    clearSelection,
    commitSelectPlate,
    demoSelectPlate,
    moves: currentPuzzle?.correctMoves ?? [],
    reset,
  })

  // Accepted-challenge opening: programmatically trigger the SAME logic as
  // pressing the (hidden) hint button `hints_used` times — the from-scratch
  // reveal replays the first `advancedBy` correct moves with the normal flight
  // animation. While it runs, `isPlaying` blocks all user input. Runs ONCE on
  // mount; afterwards `reset` targets the advanced board, so it never re-runs.
  const preRollRunningRef = useRef(false)
  const preRollSeenPlayingRef = useRef(false)

  useEffect(()=> {
    if (advancedBy <= 0) {
      setIsAnimatingOpen(false)
      return
    }
    if (preRolledRef.current) return
    preRolledRef.current = true
    if ((currentPuzzle?.correctMoves ?? []).length === 0) {
      setIsAnimatingOpen(false)
      return
    }
    preRollRunningRef.current = true
    startReplay({ count: advancedBy })
  }, [advancedBy, currentPuzzle, startReplay])

  // When the reveal runs its course (isPlaying observed true, then false),
  // flip the reset target to the advanced board the acceptor starts from.
  // The "observed true" guard matters: the trigger effect above starts the
  // replay in the same commit this effect reads `isPlaying`, so without it
  // the flip would happen instantly and skip the animation.
  useEffect(()=> {
    if (!preRollRunningRef.current) return
    if (isPlaying) {
      preRollSeenPlayingRef.current = true
      return
    }
    if (preRollSeenPlayingRef.current) {
      preRollRunningRef.current = false
      preRollSeenPlayingRef.current = false
      setIsAnimatingOpen(false)
    }
  }, [isPlaying])

  const solved = useMemo(()=> isSolved(board), [board])

  // Near-win marker — computed only after the move settles (no ball in flight)
  // and from NEAR_WIN_MIN_MOVES moves on, keeping the check off the animation
  // path (see SortaballsGame.tsx).
  const NEAR_WIN_ENABLED = true
  const NEAR_WIN_MIN_MOVES = 35
  const nearWinEligible =
    NEAR_WIN_ENABLED && moveCount >= NEAR_WIN_MIN_MOVES && movingBall === null
  const nearSolved = useMemo(
    ()=> (nearWinEligible ? isOneMoveFromSolved(board, capacity) : false),
    [board, capacity, nearWinEligible],
  )
  const successReached = solved || nearSolved

  // Save progress when puzzle is solved
  useEffect(()=> {
    if (successReached && currentPuzzle && moveCount > 0) {
      saveProgress(currentPuzzle.boardId, moveCount)
    }
  }, [successReached, currentPuzzle, moveCount])

  // Celebrate on the near-win or the real solve: confetti + a haptic pulse on
  // mobile. Fires once — `successReached` flips true a move early and stays
  // true through the final move, so the deps are stable afterwards.
  useEffect(()=> {
    if (successReached && moveCount > 0) {
      fireSuccessFeedbackCanvas(undefined, true)
    }
  }, [successReached, moveCount])

  // Help replays the puzzle's opening correct moves as REAL committed moves
  // (commit mode): they count toward moveCount, are undoable and are persisted,
  // so a reload restores the revealed board and its history.
  const handleHelp = ()=> {
    startReplay({ commit: true })
  }

  // Persist the in-progress board (and the committed moves that make restore
  // replayable) once the player has made their first move, so a reload (or
  // returning to this puzzle) resumes where they left off. Debounced — rapid
  // moves coalesce into one write instead of re-serializing the whole session
  // on every commit. Skipped during challenge/battle and once a win is reached.
  useCurrentPuzzleBoardPersist({
    board,
    boardId: currentPuzzle ? currentPuzzle.boardId : null,
    moves,
    paused: !!battle || successReached,
  })

  // A win clears the saved board — replaying the puzzle starts from scratch.
  useEffect(()=> {
    if (successReached) clearCurrentPuzzle()
  }, [successReached])

  // A user reset abandons the in-progress session: clear it so a later reload
  // starts from the original board instead of resurrecting it.
  const handleReset = ()=> {
    reset()
    clearCurrentPuzzle()
  }

  // Auto-playing guided tour — shared with every sortaballs variant, driving
  // real selections/moves on this board. Off in battle (challenges) mode.
  const tour = useGameplayTour({
    board,
    capacity,
    clearSelection,
    demoHelp: handleHelp,
    demoReset: handleReset,
    demoSelectPlate,
    demoUndo: undo,
    disabled: !!battle || !currentPuzzle,
    isRevealPlaying: isPlaying,
    moveCount,
    selectAll,
  })

  const handlePreviousPuzzle = ()=> {
    const prevPuzzle = getPuzzleById(currentPuzzleId - 1)
    if (!prevPuzzle) return
    sortaballs8ColorsLevelStore.setLevel(prevPuzzle.id)
  }

  const handleNextPuzzle = ()=> {
    const nextPuzzle = getPuzzleById(currentPuzzleId + 1)
    if (!nextPuzzle) return
    sortaballs8ColorsLevelStore.setLevel(nextPuzzle.id)
  }

  // Calculate if we can navigate
  const canGoPrevious = getPuzzleById(currentPuzzleId - 1) !== undefined
  const canGoNext = getPuzzleById(currentPuzzleId + 1) !== undefined

  // E2E hook: the root carries `data-success-reached` (solved OR one move from
  // solved) so e2e can observe the near-win trigger without a visible block —
  // the confetti burst is the only success feedback.
  // The board is shared by both layouts: normal play embeds it in the header's
  // centre rail, battle keeps the flat top header with the board beneath.
  // Stable plate handlers: while a reveal plays (or the board is won) taps are
  // dropped; otherwise they route to the engine's selectors. `useCallback`
  // keeps the identities stable across renders so memoized Plate/Board skip
  // re-renders for unrelated state changes.
  const handlePlateClick = useCallback(
    (index: number)=> {
      if (isPlaying || successReached) return
      selectPlate(index)
    },
    [isPlaying, selectPlate, successReached],
  )
  const handlePlateLongPress = useCallback(
    (index: number)=> {
      if (isPlaying || successReached) return
      selectAll(index)
    },
    [isPlaying, selectAll, successReached],
  )

  const boardElement = (
    <Board
      board={board}
      capacity={capacity}
      flightKey={flightKey}
      groupSize={groupSize}
      holdCueIndex={tour.holdCueIndex}
      movingBall={movingBall}
      onMovingBallDone={clearMovingBall}
      onPlateClick={handlePlateClick}
      onPlateLongPress={handlePlateLongPress}
      selectedPlate={selectedPlate}
      selectionCount={selectionCount}
    />
  )

  return (
    <div
      className="
        ball-sort flex min-h-dvh flex-col items-center justify-center bg-primary-background p-8
      "
      data-success-reached={successReached}
    >
      {currentPuzzle ? (
        <SharedPuzzleHeader
          battle={battle}
          canHelp={(currentPuzzle.correctMoves?.length ?? 0) > 0 && !hideHints}
          center={battle ? undefined : boardElement}
          checkIfSolved={checkIfSolved}
          disabled={isPlaying || battle?.paused}
          hasNext={canGoNext}
          hasPrevious={canGoPrevious}
          hideHelp={hideHints}
          highlightButton={tour.highlightButton}
          moveCount={moveCount}
          onHelp={handleHelp}
          onNext={handleNextPuzzle}
          onPrevious={handlePreviousPuzzle}
          onReset={handleReset}
          onUndo={undo}
          puzzle={currentPuzzle}
          solved={successReached && moveCount > 0}
          totalPuzzles={getTotalPuzzleCount()}
        />
      ) : (
        <h1 className="mb-6 text-3xl font-bold text-foreground">
          Ball Sort
        </h1>
      )}
      {/* Battle mode keeps the flat top header, so the board goes underneath it
          rather than inside the header's centre. */}
      {battle && boardElement}

      {battle?.paused && (
        <div
          aria-label="Resume game"
          className="battle-pause-overlay"
          onClick={()=> battle.onTogglePause?.()}
          onKeyDown={event=> {
            if (event.key === "Enter" || event.key === " ") {
              battle.onTogglePause?.()
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Play size={56} />
        </div>
      )}

      <GameplayTourView
        canAdvance={tour.canAdvance}
        moveCount={tour.moveCount}
        onCancelStart={tour.cancelStart}
        onClose={tour.close}
        onConfirmStart={tour.confirmStart}
        onContinue={tour.continue}
        pendingStartConfirm={tour.pendingStartConfirm}
        stepText={tour.stepText}
      />
    </div>
  )
}
